import Link from '@/components/Link';
import { useTaxonomies } from '@gik/api/give/taxonomies';
import type WordpressSituation from '@gik/core/models/wordpress/WordpressSituation';
import type { UIComponent } from '@gik/core/types/UI';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { GiveSituationTile } from '@gik/shop/components/SituationsGrid/GiveSituationTile';
import { getSituationVariant } from '@gik/shop/utils/getSituationVariant';
import { Button } from '@gik/ui/Button';
import { Grid } from '@gik/ui/Grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';

export interface ISamplePage {
  title: string;
  imageUrl: string;
}
export type SamplePagesGridProps = {
  // can pass initial or mock data
  items?: WordpressSituation[];
  cols?: number;
  perPage?: number;
  loadMoreButton?: boolean;
  skeleton?: boolean;
  skeletonItems?: number;
} & UIComponent;

export function SamplePagesGridComp({
  perPage = 5,
  cols = 5,
  loadMoreButton,
  items,
  className,
}: SamplePagesGridProps): React.ReactElement {
  const bem = bemBlock('sample-pages-grid');
  const { t } = useTranslation();

  const [page, setPage] = React.useState<number>(1);
  const totalPages = 10;

  const { data: taxonomies } = useTaxonomies('situation');

  function handleLoadMore() {
    // TODO: this should perform backend pagination when our rest api and cache server supports it
    // for now just show all situations in one go and hide the load more button
    setPage(totalPages);
  }

  if (items?.length === 0) {
    return <div className={bem('empty')}>{t(translationKeys.empty)}</div>;
  }

  return (
    <div>
      <Grid cols={cols} className={bem(null, null, className)}>
        {items?.map((item, index) => {
          if (index >= page * perPage) return null;
          return (
            <Link key={index} href={item.acf.sample_inkind_page_url + '?sample-page=true'} className={bem('item')}>
              <GiveSituationTile
                variant={getSituationVariant(item, taxonomies)}
                title={item.name}
                imageUrl={item.acf.taxonomy_svg_icon}
              />
            </Link>
          );
        })}
      </Grid>

      {loadMoreButton && page < totalPages && (
        <Button onClick={handleLoadMore} variant="default" pill>
          Load more
        </Button>
      )}
    </div>
  );
}

export const SamplePagesGrid = withComponentErrorBoundary(SamplePagesGridComp);

export const SamplePagesGridSkeleton = ({
  skeletonItems = 10,
  cols = 5,
  loadMoreButton,
}: Partial<SamplePagesGridProps>): React.ReactElement => {
  const bem = bemBlock(`sample-pages-grid`);
  return (
    <div>
      <Grid cols={cols} className={`${bem()}`}>
        {Array.from(Array(skeletonItems).keys()).map(index => {
          return <GiveSituationTile skeleton key={index} />;
        })}
      </Grid>
      {loadMoreButton && (
        <Button disabled variant="default" pill>
          Load more
        </Button>
      )}
    </div>
  );
};
